import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import storePricesService from "../../api/services/storePricesService";
import PaginationBar from "../../components/common/PaginationBar";
import { TableSkeleton } from "../../components/common/skeletons";
import useClientPagination from "../../hooks/useClientPagination";

const PRICES_PAGE_SIZE = 25;

const TH_CLASS =
  "px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap";
const TD_CLASS = "px-4 sm:px-6 py-3 sm:py-4 text-xs md:text-sm whitespace-nowrap";

const INPUT_CLS =
  "w-full text-sm border border-gray-300 rounded-lg px-4 py-2.5 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 shadow-sm";

const HIGHLIGHT_CODES = new Set(["US", "USA", "PK", "PAK", "AE", "ARE", "GB", "GBR", "SA", "SAU", "IN", "IND"]);

const TABS = [
  { key: "appstore", label: "App Store" },
  { key: "playstore", label: "Play Store" },
];

function getAdminRole() {
  return localStorage.getItem("adminUserRole") || "";
}

function canSeeProductId() {
  const role = getAdminRole();
  return role === "Admin" || role === "Developer";
}

function friendlyProductName(productId) {
  const id = String(productId || "").toLowerCase();
  if (id.includes("week")) return "Weekly Premium";
  if (id.includes("quarter") || id.includes("three") || id.includes("3")) {
    return "Quarterly Premium";
  }
  if (id.includes("month")) return "Monthly Premium";
  return productId || "Product";
}

function formatProductOptionLabel(p, showProductId) {
  const name = p.name || friendlyProductName(p.productId);
  if (!showProductId) return name;
  const plan = p.basePlanId ? ` / ${p.basePlanId}` : "";
  return `${name} (${p.productId}${plan})`;
}

function formatPrice(amount, currency) {
  if (amount == null || amount === "") return "—";
  const n = Number(amount);
  if (Number.isNaN(n)) return `${amount}${currency ? ` ${currency}` : ""}`;
  try {
    return new Intl.NumberFormat(undefined, {
      style: currency ? "currency" : "decimal",
      currency: currency || undefined,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}${
      currency ? ` ${currency}` : ""
    }`;
  }
}

function NasePricesTabs({ activeTab, onTabChange }) {
  return (
    <div className="mb-6">
      <div className="border-b border-gray-200 bg-white rounded-t-xl shadow-sm px-2 sm:px-4 pt-3 pb-0">
        <nav className="flex gap-1 flex-wrap" aria-label="Nase Prices tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => onTabChange(tab.key)}
              className={`px-4 py-2.5 rounded-t-lg font-medium text-sm transition-all duration-200 shrink-0 ${
                activeTab === tab.key
                  ? "bg-red-500 text-white shadow-md -mb-px border border-b-0 border-red-500"
                  : "text-gray-600 hover:bg-gray-100 hover:text-gray-900 border border-transparent"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
}

function FilterBar({
  products,
  productId,
  setProductId,
  loadingProducts,
  showProductId,
  search,
  setSearch,
  currency,
  setCurrency,
  currencies,
  sortBy,
  setSortBy,
  onRefresh,
  refreshing,
}) {
  const hasFilters = Boolean(search.trim()) || currency !== "all";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-end">
        <div className="lg:col-span-3">
          <label className="block text-xs font-semibold text-gray-500 mb-1.5">Plan</label>
          <select
            className={INPUT_CLS}
            value={productId}
            disabled={loadingProducts}
            onChange={(e) => setProductId(e.target.value)}
          >
            {!products.length && <option value="">No products</option>}
            {products.map((p) => (
              <option key={p.subscriptionId || p.productId} value={p.productId}>
                {formatProductOptionLabel(p, showProductId)}
              </option>
            ))}
          </select>
        </div>
        <div className="lg:col-span-3">
          <label className="block text-xs font-semibold text-gray-500 mb-1.5">Search country</label>
          <input
            className={INPUT_CLS}
            placeholder="e.g. Pakistan, US, AED…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="lg:col-span-2">
          <label className="block text-xs font-semibold text-gray-500 mb-1.5">Currency</label>
          <select className={INPUT_CLS} value={currency} onChange={(e) => setCurrency(e.target.value)}>
            <option value="all">All</option>
            {currencies.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="lg:col-span-2">
          <label className="block text-xs font-semibold text-gray-500 mb-1.5">Sort</label>
          <select className={INPUT_CLS} value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="country">Country A–Z</option>
            <option value="price">Price low → high</option>
            <option value="currency">Currency</option>
          </select>
        </div>
        <div className="lg:col-span-2 flex gap-2">
          <button
            type="button"
            className="flex-1 text-sm rounded-lg border border-red-500 bg-red-500 px-4 py-2.5 text-white font-semibold hover:bg-red-600 transition-colors shadow-sm disabled:opacity-50"
            onClick={onRefresh}
            disabled={refreshing || !productId}
          >
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
          {hasFilters ? (
            <button
              type="button"
              className="text-sm text-red-600 hover:text-red-700 font-medium px-3 py-2.5 rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap"
              onClick={() => {
                setSearch("");
                setCurrency("all");
              }}
            >
              Clear
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ filtered, prices, meta, storeLabel, showProductId }) {
  const stats = useMemo(() => {
    const currSet = new Set(filtered.map((r) => r.currency).filter(Boolean));
    const singleCurrency = currSet.size === 1 ? Array.from(currSet)[0] : null;
    const amounts = singleCurrency
      ? filtered
          .filter((r) => r.currency === singleCurrency)
          .map((r) => Number(r.customerPrice))
          .filter((n) => !Number.isNaN(n))
      : [];
    return {
      shown: filtered.length,
      total: prices.length,
      currencies: currSet.size,
      min: amounts.length ? Math.min(...amounts) : null,
      max: amounts.length ? Math.max(...amounts) : null,
      sampleCurrency: singleCurrency,
    };
  }, [filtered, prices]);

  const planName = meta?.subscriptionName || friendlyProductName(meta?.productId);

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 mb-4 pb-4 border-b border-gray-100">
      <span>
        Plan: <strong className="text-gray-800">{planName || "—"}</strong>
      </span>
      <span>
        Store: <strong className="text-gray-800">{storeLabel}</strong>
      </span>
      {showProductId && meta?.productId ? (
        <span className="font-mono text-gray-400">{meta.productId}</span>
      ) : null}
      <span>
        Showing <strong className="text-gray-800">{stats.shown}</strong>
        {stats.shown !== stats.total ? ` of ${stats.total}` : ""} countries
      </span>
      <span>
        Currencies: <strong className="text-gray-800">{stats.currencies || "—"}</strong>
      </span>
      {stats.min != null ? (
        <span>
          Range:{" "}
          <strong className="text-gray-800">
            {formatPrice(stats.min, stats.sampleCurrency)} –{" "}
            {formatPrice(stats.max, stats.sampleCurrency)}
          </strong>
        </span>
      ) : null}
    </div>
  );
}

function PricesTable({ storeLabel, meta, showProductId, filtered, prices, loading }) {
  const {
    pageItems: pagedRows,
    page,
    setPage,
    totalItems,
    totalPages,
    pageSize,
    resetPage,
  } = useClientPagination(filtered, PRICES_PAGE_SIZE);

  useEffect(() => {
    resetPage();
  }, [filtered, resetPage]);

  if (loading) {
    return <TableSkeleton rows={10} cols={4} />;
  }

  return (
    <>
      <SummaryRow
        filtered={filtered}
        prices={prices}
        meta={meta}
        storeLabel={storeLabel}
        showProductId={showProductId}
      />

      <div className="overflow-x-auto rounded-lg border border-gray-200 min-w-0">
        <table className="min-w-full divide-y divide-gray-200 bg-white">
          <thead className="bg-gray-50">
            <tr>
              <th className={TH_CLASS}>#</th>
              <th className={TH_CLASS}>Country</th>
              <th className={TH_CLASS}>Currency</th>
              <th className={`${TH_CLASS} text-right`}>Customer pays</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {!filtered.length ? (
              <tr>
                <td colSpan={4} className="px-6 py-12 text-center text-sm text-gray-500">
                  No countries match your search or currency filter.
                </td>
              </tr>
            ) : (
              pagedRows.map((row, index) => {
                const highlight = HIGHLIGHT_CODES.has(String(row.countryCode || "").toUpperCase());
                return (
                  <tr
                    key={row.countryCode}
                    className={`hover:bg-gray-50 transition-colors ${
                      highlight ? "bg-red-50/50" : ""
                    }`}
                  >
                    <td className={`${TD_CLASS} font-medium text-gray-500 tabular-nums`}>
                      {(page - 1) * pageSize + index + 1}
                    </td>
                    <td className={TD_CLASS}>
                      <div className="font-medium text-gray-900">{row.countryName || "Unknown"}</div>
                      <div className="text-xs text-gray-400 font-mono">{row.countryCode || "—"}</div>
                    </td>
                    <td className={TD_CLASS}>
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-700">
                        {row.currency || "—"}
                      </span>
                    </td>
                    <td className={`${TD_CLASS} text-right font-semibold text-red-600 tabular-nums`}>
                      {formatPrice(row.customerPrice, row.currency)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <PaginationBar
        page={page}
        totalPages={totalPages}
        totalItems={totalItems}
        pageSize={pageSize}
        label="countries"
        onPageChange={setPage}
      />
    </>
  );
}

function usePriceFilters(prices) {
  const [search, setSearch] = useState("");
  const [currency, setCurrency] = useState("all");
  const [sortBy, setSortBy] = useState("country");

  const currencies = useMemo(() => {
    const set = new Set(prices.map((p) => p.currency).filter(Boolean));
    return Array.from(set).sort();
  }, [prices]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = prices.filter((p) => {
      if (currency !== "all" && p.currency !== currency) return false;
      if (!q) return true;
      return (
        String(p.countryName || "").toLowerCase().includes(q) ||
        String(p.countryCode || "").toLowerCase().includes(q) ||
        String(p.currency || "").toLowerCase().includes(q) ||
        String(p.customerPrice || "").includes(q)
      );
    });

    rows = [...rows].sort((a, b) => {
      if (sortBy === "price") {
        return Number(a.customerPrice || 0) - Number(b.customerPrice || 0);
      }
      if (sortBy === "currency") {
        return String(a.currency || "").localeCompare(String(b.currency || ""));
      }
      return String(a.countryName || a.countryCode || "").localeCompare(
        String(b.countryName || b.countryCode || "")
      );
    });
    return rows;
  }, [prices, search, currency, sortBy]);

  return {
    search,
    setSearch,
    currency,
    setCurrency,
    sortBy,
    setSortBy,
    currencies,
    filtered,
  };
}

function SetupBanner({ title, message, missing = [] }) {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 mb-4">
      <p className="font-semibold">{title}</p>
      <p className="mt-1 text-amber-900">{message}</p>
      {missing.length > 0 && (
        <p className="mt-2 text-xs font-mono text-amber-800">
          Missing in backend `.env`: {missing.join(", ")}
        </p>
      )}
      <p className="mt-2 text-xs text-amber-800">
        Add the values from App Store Connect → Users and Access → Integrations → App Store Connect API, then restart
        the backend server.
      </p>
    </div>
  );
}

function AppStoreTab() {
  const showProductId = canSeeProductId();
  const [appStoreStatus, setAppStoreStatus] = useState(null);
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [meta, setMeta] = useState(null);
  const [prices, setPrices] = useState([]);
  const filters = usePriceFilters(prices);

  const isConfigured = appStoreStatus?.configured !== false;

  const loadProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const status = await storePricesService.getAppStoreStatus();
      setAppStoreStatus(status);
      if (!status?.configured) {
        setProducts([]);
        setProductId("");
        return;
      }
      const data = await storePricesService.listAppStoreProducts();
      const list = data.products || [];
      setProducts(list);
      setProductId((prev) => {
        if (prev) return prev;
        const preferred =
          list.find((p) => p.productId === "com.nuae.naseebi.Monthly") ||
          list.find((p) => String(p.productId || "").toLowerCase().includes("month")) ||
          list[0];
        return preferred?.productId || "";
      });
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message || "Failed to load App Store products");
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  const loadPrices = useCallback(async (id, { refresh = false } = {}) => {
    if (!id) {
      setPrices([]);
      setMeta(null);
      return;
    }
    setLoadingPrices(true);
    try {
      const data = await storePricesService.getAppStorePrices(
        id,
        refresh ? { refresh: true } : {}
      );
      setMeta(data);
      setPrices(data.prices || []);
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message || "Failed to load prices");
      setPrices([]);
      setMeta(null);
    } finally {
      setLoadingPrices(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    if (productId && isConfigured) loadPrices(productId);
  }, [productId, loadPrices, isConfigured]);

  return (
    <>
      {!loadingProducts && !isConfigured && (
        <SetupBanner
          title="App Store Connect is not configured"
          message={appStoreStatus?.message || "Add App Store Connect API credentials to load prices."}
          missing={appStoreStatus?.missing || ["ASC_ISSUER_ID", "ASC_KEY_ID", "ASC_PRIVATE_KEY"]}
        />
      )}
      <FilterBar
        products={products}
        productId={productId}
        setProductId={setProductId}
        loadingProducts={loadingProducts}
        showProductId={showProductId}
        search={filters.search}
        setSearch={filters.setSearch}
        currency={filters.currency}
        setCurrency={filters.setCurrency}
        currencies={filters.currencies}
        sortBy={filters.sortBy}
        setSortBy={filters.setSortBy}
        onRefresh={() => loadPrices(productId, { refresh: true })}
        refreshing={loadingPrices}
      />
      <div className="mt-5">
        <PricesTable
          storeLabel="App Store"
          meta={meta}
          showProductId={showProductId}
          filtered={filters.filtered}
          prices={prices}
          loading={isConfigured && (loadingPrices || loadingProducts)}
        />
      </div>
    </>
  );
}

function PlayStoreTab() {
  const showProductId = canSeeProductId();
  const PREFERRED = ["monthly_premium", "weekly_premium", "quarter_premium"];
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [meta, setMeta] = useState(null);
  const [prices, setPrices] = useState([]);
  const filters = usePriceFilters(prices);

  const loadProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const data = await storePricesService.listPlayStoreProducts();
      const list = data.products || [];
      setProducts(list);
      setProductId((prev) => {
        if (prev) return prev;
        const preferred = list.find((p) => PREFERRED.includes(p.productId)) || list[0];
        return preferred?.productId || "";
      });
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message || "Failed to load Play Store products");
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  const loadPrices = useCallback(async (id, { refresh = false } = {}) => {
    if (!id) {
      setPrices([]);
      setMeta(null);
      return;
    }
    setLoadingPrices(true);
    try {
      const data = await storePricesService.getPlayStorePrices(
        id,
        refresh ? { refresh: true } : {}
      );
      setMeta(data);
      setPrices(data.prices || []);
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message || "Failed to load Play prices");
      setPrices([]);
      setMeta(null);
    } finally {
      setLoadingPrices(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    if (productId) loadPrices(productId);
  }, [productId, loadPrices]);

  return (
    <>
      <FilterBar
        products={products}
        productId={productId}
        setProductId={setProductId}
        loadingProducts={loadingProducts}
        showProductId={showProductId}
        search={filters.search}
        setSearch={filters.setSearch}
        currency={filters.currency}
        setCurrency={filters.setCurrency}
        currencies={filters.currencies}
        sortBy={filters.sortBy}
        setSortBy={filters.setSortBy}
        onRefresh={() => loadPrices(productId, { refresh: true })}
        refreshing={loadingPrices}
      />
      <div className="mt-5">
        <PricesTable
          storeLabel="Play Store"
          meta={meta}
          showProductId={showProductId}
          filtered={filters.filtered}
          prices={prices}
          loading={loadingPrices || loadingProducts}
        />
      </div>
    </>
  );
}

export default function NasePricesPage() {
  const [tab, setTab] = useState("appstore");
  const activeTabLabel = TABS.find((t) => t.key === tab)?.label || "App Store";

  return (
    <div className="p-4 w-full min-w-0 overflow-x-hidden">
      <NasePricesTabs activeTab={tab} onTabChange={setTab} />

      <div className="bg-white rounded-b-xl rounded-t-none sm:rounded-t-xl shadow-md border border-t-0 border-gray-200 overflow-hidden">
        <div className="p-5 sm:p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
            <div>
              <h2 className="text-md md:text-xl font-bold text-gray-800 tracking-tight">
                Nase Prices — {activeTabLabel}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                See what customers pay for Weekly, Monthly, and Quarterly plans in each country.
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {tab === "appstore" ? <AppStoreTab /> : <PlayStoreTab />}
        </div>
      </div>
    </div>
  );
}
